/**
 * Meltdown, and the reset that comes with it.
 *
 * The reset is written as resetLayers(state, depth) rather than a single
 * hardcoded meltdown, so the planned Lobotomy layer (depth 2, resets Grey
 * Matter and skills for Cortex Shards) drops in without a rewrite.
 */

import Decimal from 'break_infinity.js';
import { CLOCK_DEFAULT, HEAT_MIN } from '../data/balance.ts';
import { GENERATORS } from '../data/generators.ts';
import { exactIdeaGain, getModifiers, type Modifiers } from './formulas.ts';
import type { GameState } from './state.ts';

/** How deep a reset goes. Higher layers subsume the ones below them. */
export const LAYER_MELTDOWN = 1;
/** Reserved for Lobotomy. Nothing in v1 passes this. */
export const LAYER_LOBOTOMY = 2;

/**
 * Wipes every layer up to `depth`.
 *
 * depth 1 - the run: thoughts, generators, clock, heat, vent.
 * depth 2 - the tree: Grey Matter and skills. Not used in v1.
 *
 * Stats, unlocks and settings are never reset at any depth.
 */
export function resetLayers(state: GameState, depth: number, mods: Modifiers): void {
  if (depth >= LAYER_MELTDOWN) {
    const retain = mods.retainFraction;
    for (let i = 0; i < GENERATORS.length; i++) {
      const owned = state.generators[i] ?? 0;
      state.generators[i] = retain > 0 ? Math.floor(owned * retain) : 0;
    }

    state.thoughts = new Decimal(0);
    state.thoughtsThisRun = new Decimal(0);
    state.clock = CLOCK_DEFAULT;
    state.heat = HEAT_MIN;
    state.peakHeatThisRun = HEAT_MIN;
    state.ventCooldown = 0;
    state.runTimeSec = 0;
    state.entities = [];
  }

  if (depth >= LAYER_LOBOTOMY) {
    state.greyMatter = new Decimal(0);
    state.skills = {};
  }
}

export interface MeltdownResult {
  gain: Decimal;
  runTimeSec: number;
  thoughtsThisRun: Decimal;
}

/**
 * Melts the rig down: banks the Grey Matter, records the run, resets.
 *
 * Always succeeds. Refusing a zero-yield meltdown is a UI decision, made where
 * the player can be told why - see meltdownWorthwhile().
 */
export function meltdown(state: GameState, mods: Modifiers = getModifiers(state)): MeltdownResult {
  // Whole ideas are banked; the fraction left over rides into the next run
  // rather than being discarded.
  const total = exactIdeaGain(state, mods).add(state.ideaRemainder);
  const gain = total.floor();
  // Normalised: subtracting equal values yields -0, which would serialise as
  // "-0" and reads as a negative amount of carried progress.
  const remainder = total.sub(gain);
  state.ideaRemainder = remainder.gt(0) ? remainder : new Decimal(0);
  const result: MeltdownResult = {
    gain,
    runTimeSec: state.runTimeSec,
    thoughtsThisRun: state.thoughtsThisRun,
  };

  state.greyMatter = state.greyMatter.add(gain);
  state.stats.meltdowns += 1;

  if (state.thoughtsThisRun.gt(state.stats.bestRunThoughts)) {
    state.stats.bestRunThoughts = state.thoughtsThisRun;
  }
  if (
    state.stats.fastestMeltdownSec === 0 ||
    (state.runTimeSec > 0 && state.runTimeSec < state.stats.fastestMeltdownSec)
  ) {
    state.stats.fastestMeltdownSec = state.runTimeSec;
  }

  resetLayers(state, LAYER_MELTDOWN, mods);
  return result;
}
